/* eslint-disable import/no-default-export */
import API from 'app/gamma/src/api';
import { normalizeEnterprise } from 'app/gamma/src/api/normalizers/enterprise';
import {
  LOAD_HEADER_SUCCESS,
  LoadHeaderSuccessAction,
} from 'app/gamma/src/modules/loaders/load-header';
import { getMe } from 'app/gamma/src/selectors/members';
import { EnterpriseResponse } from 'app/gamma/src/types/responses';
import { EnterpriseModel } from 'app/gamma/src/types/models';
import { StandardThunkAction, Dispatch } from 'app/gamma/src/types';
import { Action, actionCreator, createReducer } from '@trello/redux';
import { State } from 'app/gamma/src/modules/types';

export const FETCH_ENTERPRISES_START = Symbol('models/FETCH_ENTERPRISE_START');
export const FETCH_ENTERPRISES_SUCCESS = Symbol(
  'models/FETCH_ENTERPRISE_SUCCESS',
);
export const FETCH_ENTERPRISES_ERROR = Symbol('models/FETCH_ENTERPRISE_ERROR');

export const UPDATE_ENTERPRISE = Symbol('models/UPDATE_ENTERPRISE');

type FetchEnterpriseSuccessAction = Action<
  typeof FETCH_ENTERPRISES_SUCCESS,
  { enterprises: EnterpriseResponse[] }
>;

type UpdateEnterpriseAction = Action<
  typeof UPDATE_ENTERPRISE,
  EnterpriseResponse
>;

export const updateEnterpriseAction = actionCreator<UpdateEnterpriseAction>(
  UPDATE_ENTERPRISE,
);

export interface EnterpriseState {
  isLoading: boolean;
  collection: {
    [idEnterprise: string]: EnterpriseModel;
  };
}

const initialState: EnterpriseState = {
  isLoading: false,
  collection: {},
};

const upsertEnterprise = (
  state: EnterpriseState,
  enterprise: EnterpriseResponse,
) => ({
  ...state,
  collection: {
    ...state.collection,
    [enterprise.id]: {
      ...(state.collection[enterprise.id] || {}),
      ...normalizeEnterprise(enterprise),
    },
  },
});

export default createReducer(initialState, {
  [LOAD_HEADER_SUCCESS](state, { payload }: LoadHeaderSuccessAction) {
    if (!payload.enterprises) {
      return state;
    }

    return payload.enterprises.reduce(
      (result, enterprise) => upsertEnterprise(result, enterprise),
      state,
    );
  },

  [FETCH_ENTERPRISES_START](state) {
    return {
      ...state,
      isLoading: true,
    };
  },

  [FETCH_ENTERPRISES_SUCCESS](
    state,
    { payload }: FetchEnterpriseSuccessAction,
  ) {
    return payload.enterprises.reduce(
      (result, enterprise) => upsertEnterprise(result, enterprise),
      {
        ...state,
        isLoading: false,
      },
    );
  },

  [FETCH_ENTERPRISES_ERROR](state) {
    return {
      ...state,
      isLoading: false,
    };
  },

  [UPDATE_ENTERPRISE](state, { payload }: UpdateEnterpriseAction) {
    if (!payload) {
      return state;
    }

    return upsertEnterprise(state, payload);
  },
});

export const fetchMyEnterprises = (): StandardThunkAction => {
  return async (dispatch: Dispatch, getState: () => State) => {
    const state = getState();
    const me = getMe(state);
    if (!me) {
      return;
    }

    try {
      const { idEnterprisesAdmin = [] } = me;
      const enterprises = state.models.enterprises.collection;
      const promises = idEnterprisesAdmin.reduce(
        (result: Promise<EnterpriseResponse>[], idEnterprise: string) => {
          if (!enterprises[idEnterprise]) {
            result.push(
              API.client.rest.get<EnterpriseResponse>(
                `enterprises/${idEnterprise}`,
              ),
            );
          }

          return result;
        },
        [] as Promise<EnterpriseResponse>[],
      );

      if (!promises.length) {
        return;
      }

      dispatch(actionCreator(FETCH_ENTERPRISES_START)());

      const responses = await Promise.all(promises);

      dispatch(
        actionCreator(FETCH_ENTERPRISES_SUCCESS)({ enterprises: responses }),
      );
    } catch (err) {
      dispatch(actionCreator(FETCH_ENTERPRISES_ERROR)());
    }
  };
};
